import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Input,
    Text,
    Textarea,
    RadioGroup,
    Radio,
    Stack,
    Flex,
    Checkbox,
    Box,
    FormErrorMessage
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from "react-i18next";
import InlineErrorInput from "../InlineErrorInput/InlineErrorInput";

const TicketModal = ({ isOpen, onClose, onSave, initialData, eventEndISO }) => {
    const { t } = useTranslation("default");

    const [ticket, setTicket] = useState({
        id: null,
        name: '',
        price: '',
        vatPercentage: '',
        availableQuantity: '',
        maxTicketsPerOrder: '',
        isLimitedTime: true,
        saleStartDate: '',
        saleStartTime: '',
        saleEndDate: '',
        saleEndTime: '',
        description: '',
        isPersonalized: false,
        isFree: false,
        isHidden: false
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const requiredFields = [
        'name',
        ...(ticket.isFree ? [] : ['price', 'vatPercentage']),
        'availableQuantity',
        'maxTicketsPerOrder',
        ...(ticket.isLimitedTime ? ['saleStartDate', 'saleStartTime', 'saleEndDate', 'saleEndTime'] : [])
    ];

    const handleTicketKindChange = (val) => {
        setTicket(prev => ({
            ...prev,
            isFree: val === 'free',
            isPersonalized: val === 'personalized'
        }));
    };
    const limitedTimeFields = ['saleStartDate', 'saleStartTime', 'saleEndDate', 'saleEndTime'];

    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        setErrors(validate()); // liczymy wszystko, ale pokażemy tylko tam, gdzie touched=true
    };

    useEffect(() => {
        if (!isOpen) return;
        const d = initialData ?? {};

        const saleStart = d.saleStart ? dayjs(d.saleStart) : null;
        const saleEnd = d.saleEnd ? dayjs(d.saleEnd) : null;

        const dateStartStr = saleStart?.isValid() ? saleStart.format('YYYY-MM-DD') : (d.saleStartDate ?? '');
        const timeStartStr = saleStart?.isValid() ? saleStart.format('HH:mm') : (d.saleStartTime ?? '');
        const dateEndStr = saleEnd?.isValid() ? saleEnd.format('YYYY-MM-DD') : (d.saleEndDate ?? '');
        const timeEndStr = saleEnd?.isValid() ? saleEnd.format('HH:mm') : (d.saleEndTime ?? '');

        // Jeśli mamy daty ISO, to na pewno jest to czas ograniczony
        const isLimited = (d.saleStart || d.saleEnd) ? true : (d.isLimitedTime ?? true);

        setTicket({
            id: d.id ?? null,
            name: d.name ?? '',
            price: d.price?.toString() ?? '',
            vatPercentage: d.vatPercentage?.toString() ?? '',
            availableQuantity: d.availableQuantity?.toString() ?? '',
            maxTicketsPerOrder: d.maxTicketsPerOrder?.toString() ?? '',
            isLimitedTime: isLimited,
            saleStartDate: dateStartStr,
            saleStartTime: timeStartStr,
            saleEndDate: dateEndStr,
            saleEndTime: timeEndStr,
            description: d.description ?? '',
            isPersonalized: d.isPersonalized ?? false,
            isFree: d.isFree ?? false,
            isHidden: d.isHidden ?? false,
        });
        setErrors({});
        setTouched({});
    }, [isOpen, initialData]);

    useEffect(() => setErrors(validate()), [ticket])

    const handleChange = (field, value) => {
        setTicket(prev => ({ ...prev, [field]: value }));
        if (field === 'isLimitedTime' && !value) {
            setTouched(p => {
                const clone = { ...p };
                limitedTimeFields.forEach(f => delete clone[f]);
                return clone;
            });
            setErrors(e => {
                const clone = { ...e };
                limitedTimeFields.forEach(f => delete clone[f]);
                return clone;
            });
        }
    };

    const validate = () => {
        const e = {};
        const price = Number(ticket.price);
        const vat = Number(ticket.vatPercentage);
        const qty = Number(ticket.availableQuantity);
        const maxPerOrder = Number(ticket.maxTicketsPerOrder);

        if (!ticket.name.trim()) e.name = t('validation.required');

        if (!ticket.isFree) {
            if (ticket.price === '' || Number.isNaN(price)) e.price = t('ticket.validation.price_required');
            else if (price < 0) e.price = t('ticket.validation.price_negative');

            if (ticket.vatPercentage === '' || Number.isNaN(vat)) e.vatPercentage = t('ticket.validation.vat_required');
            else if (vat < 0 || vat > 100) e.vatPercentage = t('ticket.validation.vat_range');
        }

        if (ticket.availableQuantity === '' || Number.isNaN(qty)) e.availableQuantity = t('ticket.validation.quantity_required');
        else if (!Number.isInteger(qty) || qty < 0) e.availableQuantity = t('ticket.validation.quantity_integer');

        if (ticket.maxTicketsPerOrder === '' || Number.isNaN(maxPerOrder)) e.maxTicketsPerOrder = t('ticket.validation.max_tickets_required');
        else if (!Number.isInteger(maxPerOrder) || maxPerOrder <= 0) e.maxTicketsPerOrder = t('ticket.validation.max_tickets_positive');

        const hasStart = !!ticket.saleStartDate && !!ticket.saleStartTime;
        const hasEnd = !!ticket.saleEndDate && !!ticket.saleEndTime;
        if (ticket.isLimitedTime) {
            if (!ticket.saleStartDate) e.saleStartDate = t('validation.required');
            if (!ticket.saleStartTime) e.saleStartTime = t('validation.required');
            if (!ticket.saleEndDate) e.saleEndDate = t('validation.required');
            if (!ticket.saleEndTime) e.saleEndTime = t('validation.required');

            if (hasStart && hasEnd) {
                const start = dayjs(`${ticket.saleStartDate}T${ticket.saleStartTime}`);
                const end = dayjs(`${ticket.saleEndDate}T${ticket.saleEndTime}`);

                if (start.isValid() && end.isValid() && end.isBefore(start)) {
                    e.saleEndDate = t('validation.date.end_before_start');
                    e.saleEndTime = t('validation.date.end_before_start');
                }
            }

            if (eventEndISO) {
                const eventEnd = dayjs(eventEndISO);
                if (eventEnd.isValid()) {
                    if (hasEnd) {
                        const saleEnd = dayjs(`${ticket.saleEndDate}T${ticket.saleEndTime}`);
                        if (saleEnd.isValid() && saleEnd.isAfter(eventEnd)) {
                            e.saleEndDate = t('ticket.validation.sale_end_after_event');
                            e.saleEndTime = t('ticket.validation.sale_end_after_event');
                        }
                    }
                    if (hasStart) {
                        const saleStart = dayjs(`${ticket.saleStartDate}T${ticket.saleStartTime}`);
                        if (saleStart.isValid() && saleStart.isAfter(eventEnd)) {
                            e.saleStartDate = t('ticket.validation.sale_start_after_event');
                            e.saleStartTime = t('ticket.validation.sale_start_after_event');
                        }
                    }
                }
            }
        }

        return e;
    };

    const handleSubmit = () => {
        const e = validate();
        setErrors(e);
        if (Object.keys(e).length > 0) {
            // pokaż błędy tylko na polach wymaganych
            const toTouch = [...requiredFields];
            setTouched(prev => {
                const next = { ...prev };
                toTouch.forEach(f => next[f] = true);
                return next;
            });
            return; // przerwij zapis
        }

        const hasStart = !!ticket.saleStartDate && !!ticket.saleStartTime;
        const hasEnd = !!ticket.saleEndDate && !!ticket.saleEndTime;

        const saleStart = hasStart ? dayjs(`${ticket.saleStartDate}T${ticket.saleStartTime}`).toISOString() : null;
        const saleEnd = hasEnd ? dayjs(`${ticket.saleEndDate}T${ticket.saleEndTime}`).toISOString() : null;

        const ticketData = {
            id: ticket.id,
            name: ticket.name,
            price: ticket.isFree ? 0 : parseFloat(ticket.price),
            vatPercentage: ticket.isFree ? 0 : parseFloat(ticket.vatPercentage),
            availableQuantity: parseInt(ticket.availableQuantity),
            maxTicketsPerOrder: parseInt(ticket.maxTicketsPerOrder),
            isLimitedTime: ticket.isLimitedTime,
            saleStart, saleStartDate: ticket.saleStartDate, saleStartTime: ticket.saleStartTime,
            saleEnd, saleEndDate: ticket.saleEndDate, saleEndTime: ticket.saleEndTime,
            description: ticket.description,
            isPersonalized: ticket.isPersonalized,
            isFree: ticket.isFree,
            isHidden: ticket.isHidden
        };

        onSave(ticketData);
        onClose();
    };

    const grossPrice = () => {
        const basePrice = parseFloat(ticket.price) || 0;
        const vat = parseFloat(ticket.vatPercentage) || 0;
        const total = basePrice * (1 + vat / 100);
        return `${total.toFixed(2)} ${t('currency.pln')}`;
    };

    const isReq = (field) =>
        requiredFields.includes(field);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl">
            <ModalOverlay />
            <ModalContent className='ticket-modal'>
                <ModalHeader bgColor={'#F8F5F4'}>{t('ticket.header')}</ModalHeader>
                <ModalCloseButton />
                <ModalBody bgColor={'#F8F5F4'}>

                    <Flex flexDirection={"column"} gap={'16px'}>
                        <Flex flexDirection={"column"} bgColor={"white"} p={'16px'} borderRadius={'12px'} gap={'8px'}>
                            <Text className='info'>{t('ticket.info')}</Text>

                            <InlineErrorInput label={t('ticket.form.name')} value={ticket.name} placeholder={t('ticket.form.placeholder.name')}
                                onChange={e => handleChange('name', e.target.value)} onBlur={() => handleBlur('name')} useFormik={false}
                                error={errors.name} touched={touched.name} isRequired={isReq('name')}
                            />
                            {!ticket.isFree && (
                                <Flex gap={4} align="flex-end" alignItems={"flex-start"}>
                                    <InlineErrorInput flex={1}
                                        type="number" step="0.01" inputMode="decimal" label={t('ticket.form.price')}
                                        value={ticket.price} useFormik={false}
                                        onChange={e => handleChange('price', e.target.value)}
                                        onBlur={() => handleBlur('price')}
                                        error={errors.price} touched={touched.price} isRequired={isReq('price')}
                                    />
                                    <Box pb={2} fontSize="xl" alignSelf={"end"}>+</Box>

                                    <InlineErrorInput flex={1}
                                        type="number" step="0.01" inputMode="decimal" label={t('ticket.form.tax')}
                                        value={ticket.vatPercentage} useFormik={false}
                                        onChange={e => handleChange('vatPercentage', e.target.value)}
                                        onBlur={() => handleBlur('vatPercentage')}
                                        error={errors.vatPercentage} touched={touched.vatPercentage} isRequired={isReq('vatPercentage')}
                                    />
                                    <Box pb={2} fontSize="xl" alignSelf={"end"}>=</Box>

                                    <InlineErrorInput flex={1}
                                        type="text" label={t('ticket.form.final_price')}
                                        value={grossPrice()} useFormik={false} isReadOnly pr="3rem"
                                        error={errors.finalPrice} touched={touched.price || touched.vatPercentage} isRequired={isReq('price')}
                                    />
                                </Flex>
                            )}
                            <Flex gap={4} alignItems={"flex-start"}>
                                <InlineErrorInput flex={1} type="number" inputMode="numeric" label={t('ticket.form.availableQuantity')}
                                    value={ticket.availableQuantity} useFormik={false}
                                    onChange={e => handleChange('availableQuantity', e.target.value)}
                                    onBlur={() => handleBlur('availableQuantity')}
                                    error={errors.availableQuantity} touched={touched.availableQuantity} isRequired={isReq('availableQuantity')}
                                />

                                <InlineErrorInput flex={1} type="number" inputMode="numeric" label={t('ticket.form.maxTicketsPerOrder')}
                                    value={ticket.maxTicketsPerOrder} useFormik={false}
                                    onChange={e => handleChange('maxTicketsPerOrder', e.target.value)}
                                    onBlur={() => handleBlur('maxTicketsPerOrder')}
                                    error={errors.maxTicketsPerOrder} touched={touched.maxTicketsPerOrder} isRequired={isReq('maxTicketsPerOrder')}
                                />
                            </Flex>
                        </Flex>

                        <Flex flexDirection={"column"} bgColor={"white"} p={'16px'} borderRadius={'12px'} gap={'8px'}>
                            <Text className='info'>{t('ticket.form.temporary')}</Text>

                            <Flex mb={4}>
                                <Box
                                    px={4}
                                    py={'16px'}
                                    flex="1"
                                    textAlign={"center"}
                                    borderBottom={!ticket.isLimitedTime ? '3px solid #7A1F35' : '3px solid #ECE4E4'}
                                    cursor="pointer"
                                >
                                    <Checkbox
                                        isChecked={!ticket.isLimitedTime}
                                        onChange={e => handleChange('isLimitedTime', !e.target.checked)}
                                    >
                                        {t('ticket.unlimited_time')}
                                    </Checkbox>
                                </Box>
                                <Box
                                    px={4}
                                    py={'16px'}
                                    flex="1"
                                    textAlign={"center"}
                                    borderBottom={ticket.isLimitedTime ? '3px solid #7A1F35' : '3px solid #ECE4E4'}
                                    cursor="pointer"
                                >
                                    <Checkbox
                                        isChecked={ticket.isLimitedTime}
                                        onChange={e => handleChange('isLimitedTime', e.target.checked)}
                                    >
                                        {t('ticket.limited_time')}
                                    </Checkbox>
                                </Box>
                            </Flex>

                            {ticket.isLimitedTime && (
                                <Flex gap={4} alignItems={"flex-start"}>
                                    <FormControl mb={4} flex={1} isInvalid={touched.saleStartDate && !!errors.saleStartDate} isRequired={isReq('saleStartDate')}>
                                        <FormLabel>{t('ticket.form.saleStartDate')}</FormLabel>
                                        <Input
                                            type="date"
                                            value={ticket.saleStartDate}
                                            onChange={e => handleChange('saleStartDate', e.target.value)}
                                            onBlur={() => handleBlur('saleStartDate')}
                                        />
                                        <FormErrorMessage>{errors.saleStartDate}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl mb={4} flex={1} isInvalid={touched.saleStartTime && !!errors.saleStartTime} isRequired={isReq('saleStartTime')}>
                                        <FormLabel>{t('ticket.form.saleStartTime')}</FormLabel>
                                        <Input
                                            type="time"
                                            value={ticket.saleStartTime}
                                            onChange={e => handleChange('saleStartTime', e.target.value)}
                                            onBlur={() => handleBlur('saleStartTime')}
                                        />
                                        <FormErrorMessage>{errors.saleStartTime}</FormErrorMessage>
                                    </FormControl>
                                </Flex>
                            )}
                            {ticket.isLimitedTime && (
                                <Flex gap={4} alignItems={"flex-start"}>
                                    <FormControl mb={4} flex={1} isInvalid={touched.saleEndDate && !!errors.saleEndDate} isRequired={isReq('saleEndDate')}>
                                        <FormLabel>{t('ticket.form.saleEndDate')}</FormLabel>
                                        <Input
                                            type="date"
                                            value={ticket.saleEndDate}
                                            onChange={e => handleChange('saleEndDate', e.target.value)}
                                            onBlur={() => handleBlur('saleEndDate')}
                                        />
                                        <FormErrorMessage>{errors.saleEndDate}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl mb={4} flex={1} isInvalid={touched.saleEndTime && !!errors.saleEndTime} isRequired={isReq('saleEndTime')}>
                                        <FormLabel>{t('ticket.form.saleEndTime')}</FormLabel>
                                        <Input
                                            type="time"
                                            value={ticket.saleEndTime}
                                            onChange={e => handleChange('saleEndTime', e.target.value)}
                                            onBlur={() => handleBlur('saleEndTime')}
                                        />
                                        <FormErrorMessage>{errors.saleEndTime}</FormErrorMessage>
                                    </FormControl>
                                </Flex>
                            )}
                        </Flex>

                        <Flex flexDirection={"column"} bgColor={"white"} p={'16px'} borderRadius={'12px'} gap={'8px'}>
                            <Text className='info'>{t('ticket.form.extra.info')}</Text>

                            <FormControl mb={4}>
                                <FormLabel>{t('ticket.form.extra.description')}</FormLabel>
                                <Textarea value={ticket.description} onChange={e => handleChange('description', e.target.value)}
                                    placeholder={t('ticket.form.extra.placeholder.description')} />
                            </FormControl>

                            <FormControl mb={4}>
                                <FormLabel>{t('ticket.form.extra.kind.title')}</FormLabel>
                                <RadioGroup value={ticket.isFree ? 'free' : (ticket.isPersonalized ? 'personalized' : 'standard')} onChange={handleTicketKindChange}>
                                    <Stack direction="column">
                                        <Radio value="standard">
                                            <b>{t('ticket.form.extra.kind.bearer_ticket.label')}</b> - {t('ticket.form.extra.kind.bearer_ticket.message')}
                                        </Radio>
                                        <Radio value="personalized">
                                            <b>{t('ticket.form.extra.kind.personal_ticket.label')}</b> - {t('ticket.form.extra.kind.personal_ticket.message')}
                                        </Radio>
                                        <Radio value="free">
                                            <b>{t('ticket.form.extra.kind.free_ticket.label')}</b> - {t('ticket.form.extra.kind.free_ticket.message')}
                                        </Radio>
                                    </Stack>
                                </RadioGroup>
                            </FormControl>

                            <FormControl>
                                <Checkbox
                                    isChecked={ticket.isHidden}
                                    onChange={e => handleChange('isHidden', e.target.checked)}
                                >
                                    {t('ticket.form.extra.hide_on_event_page')}
                                </Checkbox>
                            </FormControl>
                        </Flex>
                    </Flex>

                </ModalBody>

                <ModalFooter bgColor={'#F8F5F4'}>
                    <Button bg="transparent" color="brand.500" px="48px" py="25px" onClick={onClose}>{t('cancel')}</Button>
                    <Button bg="brand.500" color="white" borderRadius={'12px'} _hover={{ bg: 'brand.600' }} px="48px" py="25px" isDisabled={Object.keys(errors).length > 0}
                        onClick={handleSubmit}>{t('ticket.save')}</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default TicketModal;
